import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Card, CardHeader, CardBody, Col, Row, Badge, ButtonGroup, Button, Container,
} from 'reactstrap';
import CheckIcon from 'mdi-react/CheckIcon';
import CloseIcon from 'mdi-react/CloseIcon';
import CheckboxBlankCircleOutlineIcon from 'mdi-react/CheckboxBlankCircleOutlineIcon';
import ViewGridIcon from 'mdi-react/ViewGridIcon';
import ViewListIcon from 'mdi-react/ViewListIcon';
import { isDarkTheme } from '../../../../../shared/helpers/themeTableStyles';
import useData from './useData';
import oauthService from '../../../../../services/oauthService';
import { jsonHeader } from '../../../../../config';
import PageHeader from '../../../../../shared/components/PageHeader';
import CollapsibleSection from '../../../../Budget/components/form/shared/CollapsibleSection';
import PlatformInfo from '../../../../../shared/components/PlatformInfo';
import OnboardingSettingsModal from '../../../../../shared/components/onboardingSettings/OnboardingSettingsModal';

// Onboarding tab hashes, aligning to /customer/onboarding#<tab> (see Onboarding/home.jsx TabPane ids).
// AMC has no dedicated onboarding tab today, so it links to the Amazon tab instead.
const ONBOARDING_TAB = {
  amazon: 'amazon',
  amc: 'amazon',
  dsp: 'dsp',
  walmart: 'walmart',
  google: 'google',
  meta: 'meta',
  tiktok: 'tiktok',
  target: 'target',
  microsoft: 'microsoft',
};

const CARD_STYLE = {
  height: '100%',
  border: '1px solid rgba(0, 0, 0, 0.1)',
  borderRadius: '6px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
};

// Status color -> human readable label + Bootstrap badge color + header background/text
// color, following the convention documented in notes/API_STATUS_DASHBOARD_GUIDE.md
// (updated scheme: `yellow` has been retired; `gray` now covers both "not yet configured"
// and "gate explicitly off" — neither is a problem worth flagging; `red` is reserved for a
// live test failure on something that IS configured/on.)
// headerBg mirrors the exact Bootstrap badge color used for the same status, so the card
// header banner and its status badges always match.
const STATUS_META = {
  green: {
    label: 'Active', badge: 'success', headerBg: '#1e9e73', headerText: '#ffffff',
  },
  blue: {
    label: 'Configured - Not Tested', badge: 'info', headerBg: '#2f8fd6', headerText: '#ffffff',
  },
  gray: {
    label: 'Not Active', badge: 'secondary', headerBg: '#6c757d', headerText: '#ffffff',
  },
  red: {
    label: 'Test Failed', badge: 'danger', headerBg: '#c9192f', headerText: '#ffffff',
  },
  black: {
    label: 'Unused', badge: 'dark', headerBg: '#343a40', headerText: '#ffffff',
  },
};

// When a platform exposes multiple statuses (e.g. Amazon AD-API + SP-API), the card header
// shows the single "worst" one so the color reflects the platform's overall readiness.
// Priority (most to least severe): red > gray > blue > green > black.
const STATUS_SEVERITY = ['red', 'gray', 'blue', 'green', 'black'];

function getOverallStatus(statuses) {
  const values = (statuses || []).map(s => s.status).filter(Boolean);
  if (values.length === 0) return '';
  return STATUS_SEVERITY.find(s => values.includes(s)) || values[0];
}

function StatusBadge({ status, label }) {
  if (!status) return null;
  const meta = STATUS_META[status] || { label: status, badge: 'secondary' };
  return (
    <Badge color={meta.badge} className="mb-1" style={{ marginLeft: '6px' }}>
      {label ? `${label}: ` : ''}{meta.label}
    </Badge>
  );
}

StatusBadge.propTypes = {
  status: PropTypes.string,
  label: PropTypes.string,
};

StatusBadge.defaultProps = {
  status: '',
  label: '',
};

// "*_ad_api_status" style gate settings are toggles, not free-text values — a value of '0'
// means the platform's AD-API gate is explicitly turned OFF, which is a broken/incomplete
// state even though the setting technically "has a value". Treat that as not-configured.
const isAdApiStatusGate = settingKey => /ad[\s_-]?api[\s_-]?status/i.test(settingKey || '');

function SettingsChecklist({ settings }) {
  if (!settings || settings.length === 0) {
    return <small className="text-muted">No settings tracked for this platform yet.</small>;
  }
  // Icon convention:
  // - Configured (any setting), excluding "*_ad_api_status" gates explicitly set to '0': GREEN checkmark
  // - Not configured & required (isRequired: true), or an "*_ad_api_status" gate set to '0': RED X
  // - Not configured & optional (isRequired: false/undefined): YELLOW O
  const renderIcon = (setting) => {
    const isDisabledGate = isAdApiStatusGate(setting.settingKey) && setting.value === '0';

    if (setting.isConfigured && !isDisabledGate) {
      return <CheckIcon size={16} style={{ color: 'green', flexShrink: 0 }} />;
    }
    if (setting.isRequired || isDisabledGate) {
      return <CloseIcon size={16} style={{ color: 'red', flexShrink: 0 }} />;
    }
    return <CheckboxBlankCircleOutlineIcon size={16} style={{ color: '#e6c200', flexShrink: 0 }} />;
  };

  return (
    <ul style={{ listStyle: 'none', paddingLeft: 0, marginBottom: 0 }}>
      {settings.map((setting) => {
        const isDisabledGate = isAdApiStatusGate(setting.settingKey) && setting.value === '0';
        return (
          <li
            key={setting.settingKey}
            title={setting.value || ''}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '2px 0',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            {renderIcon(setting)}
            <span style={{ marginLeft: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <strong>{setting.settingKey}</strong>
              {setting.isConfigured && setting.value ? <span>: {setting.value}</span> : ''}
              {isDisabledGate && <span className="text-danger"> (disabled)</span>}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

SettingsChecklist.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  settings: PropTypes.array,
};

SettingsChecklist.defaultProps = {
  settings: [],
};

function PlatformDetails({
  platformKey, title, settings, notes, accountId, onSettingsSaved,
}) {
  const onboardingTab = ONBOARDING_TAB[platformKey];
  return (
    <>
      <SettingsChecklist settings={settings} />
      {notes && (
        <div className="mt-3">
          <CollapsibleSection title="Setup Info" defaultOpen={false}>
            {notes}
            {(onboardingTab || accountId) && (
              <div className="mt-3 d-flex flex-wrap" style={{ gap: '8px' }}>
                {onboardingTab && (
                  <Link className="btn btn-sm btn-outline-primary" to={`/customer/onboarding#${onboardingTab}`}>
                    Go to {title} Onboarding
                  </Link>
                )}
                {accountId && (
                  <OnboardingSettingsModal
                    platform={platformKey}
                    accountId={accountId}
                    buttonLabel={`Update ${title} Settings`}
                    buttonColor="outline-secondary"
                    onSettingsSaved={onSettingsSaved}
                  />
                )}
              </div>
            )}
          </CollapsibleSection>
        </div>
      )}
    </>
  );
}

PlatformDetails.propTypes = {
  platformKey: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  settings: PropTypes.array,
  notes: PropTypes.node,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSettingsSaved: PropTypes.func,
};

PlatformDetails.defaultProps = {
  settings: [],
  notes: null,
  accountId: null,
  onSettingsSaved: null,
};

function PlatformCardHeader({
  title, statuses, allRequiredConfigured, extraBadges,
}) {
  const theme = useSelector(state => state.theme);
  const darkMode = isDarkTheme(theme);
  const overallStatus = getOverallStatus(statuses);
  const meta = STATUS_META[overallStatus] || { headerBg: '#eeeeee', headerText: '#1c1c1c' };
  return (
    <>
      <CardHeader
        style={{
          backgroundColor: meta.headerBg,
          color: meta.headerText,
          borderBottom: 'none',
        }}
      >
        <h4 className="bold-text text-center mb-0" style={{ color: meta.headerText }}>{title}</h4>
      </CardHeader>
      <div
        className="d-flex justify-content-end align-items-center flex-wrap px-3 py-2"
        style={{
          backgroundColor: darkMode ? '#2a2d3a' : '#f7f7f7',
          borderBottom: darkMode ? '1px solid #434651' : '1px solid #eeeeee',
        }}
      >
        {allRequiredConfigured !== null && (
          // Outlined badge (not a solid color) so this "did they fill out the required
          // fields" indicator is never confused with the solid green/blue/gray/red/black
          // STATUS_META badges above, which track the actual configured/tested state.
          <Badge
            // Pass color="" (instead of e.g. "success"/"warning") — Badge defaults to
            // "secondary" otherwise, which adds a `bg-secondary` utility class marked
            // `!important` in Bootstrap and stomps our inline backgroundColor below.
            // We only want the outline/text colored, with a neutral white/dark-gray fill.
            color=""
            className="mb-1"
            style={{
              backgroundColor: darkMode ? '#3a3d4a' : '#ffffff',
              border: `1px solid ${allRequiredConfigured ? '#1e9e73' : '#e6a100'}`,
              color: allRequiredConfigured ? '#1e9e73' : '#e6a100',
            }}
          >
            {allRequiredConfigured ? 'Required Fields Set' : 'Missing Required Fields'}
          </Badge>
        )}
        {statuses.map(s => (
          <StatusBadge key={`${title}-${s.label || s.status}`} status={s.status} label={s.label} />
        ))}
        {extraBadges.map(b => (
          <Badge key={`${title}-${b.label}`} color={b.color} className="mb-1" style={{ marginLeft: '6px' }}>
            {b.label}
          </Badge>
        ))}
      </div>
    </>
  );
}

PlatformCardHeader.propTypes = {
  title: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  statuses: PropTypes.array,
  allRequiredConfigured: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  extraBadges: PropTypes.array,
};

PlatformCardHeader.defaultProps = {
  statuses: [],
  allRequiredConfigured: null,
  extraBadges: [],
};

function PlatformLoadingSpinner({ title }) {
  return (
    <div className="d-flex flex-column align-items-center justify-content-center py-4">
      <span className="spinner-border" role="status" aria-hidden="true" />
      <small className="text-muted mt-2">Loading {title} status...</small>
    </div>
  );
}

PlatformLoadingSpinner.propTypes = {
  title: PropTypes.string.isRequired,
};

function PlatformCard(props) {
  const {
    title, statuses, allRequiredConfigured, loading, extraBadges,
  } = props;
  const theme = useSelector(state => state.theme);
  const cardBodyStyle = { color: isDarkTheme(theme) ? '#dddddd' : '#212529' };
  return (
    <Col md={6} lg={4} className="mb-4">
      <Card style={CARD_STYLE}>
        <PlatformCardHeader title={title} statuses={statuses} allRequiredConfigured={allRequiredConfigured} extraBadges={extraBadges} />
        <CardBody style={cardBodyStyle}>
          {loading ? <PlatformLoadingSpinner title={title} /> : <PlatformDetails {...props} />}
        </CardBody>
      </Card>
    </Col>
  );
}

PlatformCard.propTypes = {
  title: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  statuses: PropTypes.array,
  allRequiredConfigured: PropTypes.bool,
  loading: PropTypes.bool,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSettingsSaved: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  extraBadges: PropTypes.array,
};

PlatformCard.defaultProps = {
  statuses: [],
  allRequiredConfigured: null,
  loading: false,
  accountId: null,
  onSettingsSaved: null,
  extraBadges: [],
};

function PlatformListRow(props) {
  const {
    title, statuses, allRequiredConfigured, loading, extraBadges,
  } = props;
  const theme = useSelector(state => state.theme);
  const cardBodyStyle = { color: isDarkTheme(theme) ? '#dddddd' : '#212529' };
  return (
    <Card style={{ ...CARD_STYLE, height: 'auto' }} className="mb-3">
      <PlatformCardHeader title={title} statuses={statuses} allRequiredConfigured={allRequiredConfigured} extraBadges={extraBadges} />
      <CardBody style={cardBodyStyle}>
        {loading ? <PlatformLoadingSpinner title={title} /> : <PlatformDetails {...props} />}
      </CardBody>
    </Card>
  );
}

PlatformListRow.propTypes = {
  title: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  statuses: PropTypes.array,
  allRequiredConfigured: PropTypes.bool,
  loading: PropTypes.bool,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSettingsSaved: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  extraBadges: PropTypes.array,
};

PlatformListRow.defaultProps = {
  statuses: [],
  allRequiredConfigured: null,
  loading: false,
  accountId: null,
  onSettingsSaved: null,
  extraBadges: [],
};

export default function AccountStatusWithContext() {
  const selectedSeller = useSelector(store => store.sellerSelect);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, results, error] = useData(selectedSeller, refreshKey);
  const [viewMode, setViewMode] = useState('cards');
  const user = useSelector(state => state.authentication.user);
  // Merchant Center badge: gray = no Merchant Center ID set, green = ID set and the
  // developer-registration API returned 200, red = ID set but the API did not return 200.
  // null = still checking.
  const [merchantRegistered, setMerchantRegistered] = useState(null);
  const merchantCenterIdSet = (results?.google?.settings || []).some(setting => (
    /merchant[\s_-]?center[\s_-]?id/i.test(setting.settingKey || '')
    && !/manager/i.test(setting.settingKey || '')
    && setting.isConfigured
  ));

  useEffect(() => {
    const accountId = selectedSeller?.value;
    if (!accountId || accountId < 1 || loading || !merchantCenterIdSet) {
      setMerchantRegistered(null);
      return undefined;
    }
    let cancelled = false;
    setMerchantRegistered(null);
    oauthService.getGoogleMerchantRegistration({
      accountId,
      options: {
        headers: {
          ...jsonHeader,
          authorization: user?.jwtToken ? `JWT ${user.jwtToken}` : undefined,
        },
      },
    })
      .then(() => { if (!cancelled) setMerchantRegistered(true); })
      .catch(() => { if (!cancelled) setMerchantRegistered(false); });
    return () => { cancelled = true; };
  }, [selectedSeller?.value, refreshKey, loading, merchantCenterIdSet]);

  let merchantBadgeColor = 'secondary';
  if (merchantCenterIdSet) merchantBadgeColor = merchantRegistered ? 'success' : 'danger';

  // Passed down to OnboardingSettingsModal so that, once a user successfully
  // confirms/saves platform settings, we re-run the account status check
  // (GET /accounts/api-status) so the card reflects the latest state.
  const refreshStatus = () => setRefreshKey(prev => prev + 1);

  const amazon = results?.amazon || {};
  const amc = results?.amc || {};
  const dsp = results?.dsp || {};
  const walmart = results?.walmart || {};
  const google = results?.google || {};
  const meta = results?.meta || {};
  const tiktok = results?.tiktok || {};
  const target = results?.target || {};
  const microsoft = results?.microsoft || {};

  const platforms = [
    {
      platformKey: 'amazon',
      title: 'Amazon',
      statuses: [
        { status: amazon.adApiStatus, label: 'AD-API' },
        { status: amazon.spApiStatus, label: 'SP-API' },
      ],
      settings: amazon.settings,
      notes: <PlatformInfo platform="amazon" />,
      allRequiredConfigured: amazon.allRequiredConfigured,
    },
    {
      platformKey: 'amc',
      title: 'AMC',
      statuses: [{ status: amc.status }],
      settings: amc.settings,
      notes: <PlatformInfo platform="amc" />,
      allRequiredConfigured: amc.allRequiredConfigured,
    },
    {
      platformKey: 'dsp',
      title: 'DSP',
      statuses: [{ status: dsp.status }],
      settings: dsp.settings,
      notes: <PlatformInfo platform="dsp" />,
      allRequiredConfigured: dsp.allRequiredConfigured,
    },
    {
      platformKey: 'walmart',
      title: 'Walmart',
      statuses: [
        { status: walmart.adApiStatus, label: 'AD-API' },
        { status: walmart.mpApiStatus, label: 'MP-API' },
        { status: walmart.suApiStatus, label: 'SU-API' },
      ],
      settings: walmart.settings,
      notes: <PlatformInfo platform="walmart" />,
      allRequiredConfigured: walmart.allRequiredConfigured,
    },
    {
      platformKey: 'google',
      title: 'Google',
      statuses: [{ status: google.status }],
      settings: google.settings,
      notes: <PlatformInfo platform="google" />,
      extraBadges: loading || (merchantCenterIdSet && merchantRegistered === null)
        ? [] : [{ label: 'Merchant Center', color: merchantBadgeColor }],
      allRequiredConfigured: google.allRequiredConfigured,
    },
    {
      platformKey: 'meta',
      title: 'Meta',
      statuses: [{ status: meta.status }],
      settings: meta.settings,
      notes: <PlatformInfo platform="meta" />,
      allRequiredConfigured: meta.allRequiredConfigured,
    },
    {
      platformKey: 'tiktok',
      title: 'TikTok',
      statuses: [{ status: tiktok.status }],
      settings: tiktok.settings,
      notes: <PlatformInfo platform="tiktok" />,
      allRequiredConfigured: tiktok.allRequiredConfigured,
    },
    {
      platformKey: 'target',
      title: 'Target',
      statuses: [{ status: target.status }],
      settings: target.settings,
      notes: <PlatformInfo platform="target" />,
      allRequiredConfigured: target.allRequiredConfigured,
    },
    {
      platformKey: 'microsoft',
      title: 'Microsoft',
      statuses: [{ status: microsoft.status }],
      settings: microsoft.settings,
      notes: <PlatformInfo platform="microsoft" />,
      allRequiredConfigured: microsoft.allRequiredConfigured,
    },
  ].map(platform => ({
    ...platform, loading, accountId: selectedSeller?.value, onSettingsSaved: refreshStatus,
  }));

  const viewToggle = (
    <ButtonGroup>
      <Button
        color="primary"
        outline={viewMode !== 'cards'}
        active={viewMode === 'cards'}
        onClick={() => setViewMode('cards')}
        title="Card view"
      >
        <ViewGridIcon size={18} />
      </Button>
      <Button
        color="primary"
        outline={viewMode !== 'list'}
        active={viewMode === 'list'}
        onClick={() => setViewMode('list')}
        title="List view"
      >
        <ViewListIcon size={18} />
      </Button>
    </ButtonGroup>
  );

  return (
    <Container>
      {error}
      <Row>
        <PageHeader title="Platform Configuration Status" actions={viewToggle}>
          <h5 className="page-subhead subhead">
            &nbsp;&nbsp;<span className="status__label-color status__label-color--green" />=Configured and successful live test.
            &nbsp;&nbsp;<span className="status__label-color status__label-color--blue" />=Configured (and on, if applicable), no live test yet.
            &nbsp;&nbsp;<span className="status__label-color status__label-color--gray" />=Not active — missing a required setting, or turned off.
            &nbsp;&nbsp;<span className="status__label-color status__label-color--red" />=Configured/on, but the live test failed.
            &nbsp;&nbsp;<span className="status__label-color status__label-color--black" />=Not required/used/needed.
          </h5>
          <h5 className="page-subhead subhead">
            &nbsp;&nbsp;<CloseIcon size={16} style={{ color: 'red', verticalAlign: 'text-bottom' }} />=Missing (required).
            &nbsp;&nbsp;<CheckboxBlankCircleOutlineIcon size={16} style={{ color: '#e6c200', verticalAlign: 'text-bottom' }} />=Missing (optional).
            &nbsp;&nbsp;<CheckIcon size={16} style={{ color: 'green', verticalAlign: 'text-bottom' }} />=Found (configured).
          </h5>
        </PageHeader>
      </Row>
      {viewMode === 'cards' && (
        <Row>
          {platforms.map(platform => (
            <PlatformCard key={platform.platformKey} {...platform} />
          ))}
        </Row>
      )}
      {viewMode === 'list' && (
        <Row>
          <Col md={12}>
            {platforms.map(platform => (
              <PlatformListRow key={platform.platformKey} {...platform} />
            ))}
          </Col>
        </Row>
      )}
    </Container>
  );
}
